import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BankAccountAnswer,
  OnboardingAnswers
} from '../../utils/onboardingCategoryBuilder';
import {
  AmountCurrencySelect,
  isSignedAmountInput,
  parseSignedAmount
} from './OnboardingBuilderFields';

interface OnboardingStatementsStepProps {
  answers: OnboardingAnswers;
  onChange: (answers: OnboardingAnswers) => void;
  statementFiles: Record<string, File | undefined>;
  onStatementFileChange: (accountId: string, file: File | undefined) => void;
}

const OnboardingStatementsStep: React.FC<OnboardingStatementsStepProps> = ({
  answers,
  onChange,
  statementFiles,
  onStatementFileChange
}) => {
  const { t } = useTranslation();
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [balanceDrafts, setBalanceDrafts] = useState<Record<string, string>>({});

  const activeAccounts = answers.bankAccounts.filter(
    account => account.createInActual && account.name.trim()
  );

  const updateAccount = (accountId: string, patch: Partial<BankAccountAnswer>) => {
    const bankAccounts = answers.bankAccounts.map(account =>
      account.id === accountId ? { ...account, ...patch } : account
    );
    onChange({ ...answers, bankAccounts });
  };

  if (activeAccounts.length === 0) {
    return (
      <div className="info-box">
        <p>{t('steps.statements.noAccounts', { ns: 'onboarding' })}</p>
      </div>
    );
  }

  return (
    <div className="onboarding-statements-step">
      <div className="info-box statements-minimum">
        <strong>{t('steps.statements.minimumTitle', { ns: 'onboarding' })}</strong>
        <p>{t('steps.statements.minimumDescription', { ns: 'onboarding' })}</p>
      </div>

      <div className="info-box statements-optional">
        <p>{t('steps.statements.optionalUpload', { ns: 'onboarding' })}</p>
      </div>

      {activeAccounts.map(account => {
        const file = statementFiles[account.id];
        const hasFile = Boolean(file || account.statementFileName);
        const balanceValue =
          balanceDrafts[account.id] ??
          (account.currentBalance != null ? String(account.currentBalance) : '');

        return (
          <div key={account.id} className="builder-card onboarding-statement-card">
            <div className="statement-account-header">
              <h4>{account.name}</h4>
              <span className="statement-account-type">
                {t(`steps.bankAccounts.types.${account.accountType || 'checking'}`, {
                  ns: 'onboarding'
                })}
              </span>
            </div>

            <label className="builder-field statement-balance-field">
              <span className="statement-field-label">
                {t('steps.statements.balanceLabel', { ns: 'onboarding' })}
                <span className="statement-field-badge required">
                  {t('steps.statements.balanceRequired', { ns: 'onboarding' })}
                </span>
              </span>
              <p className="builder-help">
                {t('steps.statements.balanceHelp', { ns: 'onboarding' })}
              </p>
              <div className="builder-amount-row">
                <input
                  type="text"
                  inputMode="decimal"
                  className="builder-amount-input"
                  value={balanceValue}
                  placeholder="0"
                  onChange={e => {
                    const raw = e.target.value;
                    if (!isSignedAmountInput(raw)) return;

                    setBalanceDrafts(prev => ({ ...prev, [account.id]: raw }));
                    const parsed = parseSignedAmount(raw);
                    updateAccount(account.id, {
                      currentBalance: raw.trim() === '' ? null : parsed
                    });
                  }}
                />
                <AmountCurrencySelect
                  value={account.balanceCurrency || answers.primaryCurrency || 'USD'}
                  onChange={currency => updateAccount(account.id, { balanceCurrency: currency })}
                />
              </div>
            </label>

            <div className="statement-upload-section">
              <label className="builder-field">
                <span className="statement-field-label">
                  {t('steps.statements.uploadLabel', { ns: 'onboarding' })}
                  <span className="statement-field-badge optional">
                    {t('steps.statements.uploadOptional', { ns: 'onboarding' })}
                  </span>
                </span>
                <p className="builder-help">
                  {t('steps.statements.uploadHelp', { ns: 'onboarding' })}
                </p>
              </label>
              <div
                className={`statement-upload-zone ${hasFile ? 'has-file' : ''}`}
                onClick={() => fileInputRefs.current[account.id]?.click()}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    fileInputRefs.current[account.id]?.click();
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <input
                  ref={el => {
                    fileInputRefs.current[account.id] = el;
                  }}
                  type="file"
                  accept=".csv,.xls,.xlsx"
                  className="statement-file-input"
                  onChange={e => {
                    const selected = e.target.files?.[0];
                    if (selected) {
                      onStatementFileChange(account.id, selected);
                      updateAccount(account.id, {
                        statementFileName: selected.name,
                        statementSkipped: false
                      });
                    }
                  }}
                />
                {hasFile ? (
                  <div className="statement-file-selected">
                    <span className="statement-file-icon">📄</span>
                    <span>{file?.name || account.statementFileName}</span>
                    <button
                      type="button"
                      className="link-button"
                      onClick={e => {
                        e.stopPropagation();
                        onStatementFileChange(account.id, undefined);
                        updateAccount(account.id, {
                          statementFileName: undefined,
                          statementSkipped: true
                        });
                        const input = fileInputRefs.current[account.id];
                        if (input) input.value = '';
                      }}
                    >
                      {t('steps.statements.removeFile', { ns: 'onboarding' })}
                    </button>
                  </div>
                ) : (
                  <div className="statement-upload-prompt">
                    <span className="statement-upload-icon">📤</span>
                    <span>{t('steps.statements.uploadPrompt', { ns: 'onboarding' })}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OnboardingStatementsStep;
